import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgVideo from "lightgallery/plugins/video";
import delegate from "delegate-it";
import ajax from "@/scripts/common/ajax";

import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-video.css";
import "./photo-gallery-lightbox.css";

const licenseKey = "82F2B691-7585-4DCE-999C-3B5FFDB69F93";
const LIGHTBOX_BATCH_SIZE = 60;
const PREFETCH_THRESHOLD = 8;

function escapeHtml(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function getVideoUrl(item) {
  const value = item?.video_url ?? item?.videoUrl ?? "";
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}

function toLightboxItem(item) {
  const caption = item.alt ? `<p>${escapeHtml(item.alt)}</p>` : "";
  const videoUrl = getVideoUrl(item);

  const lightboxItem = {
    src: videoUrl || item.src,
    thumb: item.src,
    alt: item.alt || "",
    subHtml: caption,
  };

  if (videoUrl && item.src) {
    lightboxItem.poster = item.src;
  }

  return lightboxItem;
}

function normalizeItems(items = []) {
  return items.filter((item) => item && item.src).map(toLightboxItem);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getCounterTotal(context) {
  if (Number.isFinite(context.total) && context.total > 0) {
    return context.total;
  }

  return context.instance?.galleryItems?.length || 0;
}

function getLightboxOuterElement(context) {
  const outer = context.instance?.outer;
  if (!outer) return document.querySelector(".lg-outer");

  if (outer instanceof Element) {
    return outer;
  }

  if (typeof outer.get === "function") {
    return outer.get();
  }

  return document.querySelector(".lg-outer");
}

function syncLightboxCounter(context) {
  if (!context.instance) return;

  const total = getCounterTotal(context);
  if (!total) return;

  const outer = getLightboxOuterElement(context);
  if (!outer) return;

  const allEl = outer.querySelector(".lg-counter-all");
  if (allEl) {
    allEl.textContent = String(total);
  }

  const currentEl = outer.querySelector(".lg-counter-current");
  if (currentEl) {
    const current = clamp((context.instance.index ?? 0) + 1, 1, total);
    currentEl.textContent = String(current);
  }
}

async function fetchStreamBatch(streamUrl, offset, limit) {
  const url = new URL(streamUrl, window.location.origin);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));

  return ajax.get(url.toString()).json();
}

async function loadMoreSlides(context) {
  if (context.loading || context.done) return;

  context.loading = true;
  const requestOffset = context.offset;

  try {
    const res = await fetchStreamBatch(context.streamUrl, requestOffset, context.limit);
    const incoming = normalizeItems(res.items);

    if (Number.isFinite(Number(res.total))) {
      context.total = Number(res.total);
    }

    const nextOffset = requestOffset + context.limit;
    context.offset = nextOffset;

    if (!incoming.length) {
      if (!Number.isFinite(context.total) || nextOffset >= context.total) {
        context.done = true;
      }
      syncLightboxCounter(context);
      return;
    }

    const existing = [...context.instance.galleryItems];
    const updated = [...existing, ...incoming];
    context.instance.updateSlides(updated, context.instance.index);
    syncLightboxCounter(context);

    if (Number.isFinite(context.total) && nextOffset >= context.total) {
      context.done = true;
    }
  } catch (e) {
    console.error("Photo gallery lightbox fetch error:", e);
  } finally {
    context.loading = false;
  }
}

function openPhotoGalleryLightbox(detail, hostEl) {
  const initialItems = normalizeItems(detail.items);
  if (!initialItems.length) return;

  hostEl.dispatchEvent(
    new CustomEvent("photo-gallery:pause", {
      bubbles: true,
    }),
  );

  const startIndex = clamp(Number.isInteger(detail.index) ? detail.index : 0, 0, initialItems.length - 1);

  const total = Number(detail.total);
  const startOffset = Number(detail.offset);
  const context = {
    instance: null,
    streamUrl: detail.streamUrl,
    limit: LIGHTBOX_BATCH_SIZE,
    offset: Number.isFinite(startOffset) ? startOffset : initialItems.length,
    total: Number.isFinite(total) ? total : Infinity,
    loading: false,
    done: false,
    beforeSlideHandler: null,
    afterSlideHandler: null,
    afterCloseHandler: null,
    hostEl,
  };

  const lg = lightGallery(hostEl, {
    plugins: [lgThumbnail, lgVideo],
    licenseKey,
    index: startIndex,
    dynamic: true,
    dynamicEl: initialItems,
    thumbnail: true,
    download: false,
    hideBarsDelay: 5000,
    showBarsAfter: 0,
    autoplayVideoOnSlide: true,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false,
      hideBarsDelay: 0,
      showBarsAfter: 0,
    },
    youTubePlayerParams: {
      modestbranding: 1,
      showinfo: 0,
      controls: 1,
      mute: 0,
      rel: 0,
    },
  });
  context.instance = lg;

  context.beforeSlideHandler = (event) => {
    const nextIndex = event.detail?.index ?? 0;
    const threshold = context.instance.galleryItems.length - PREFETCH_THRESHOLD;

    if (nextIndex >= threshold) {
      loadMoreSlides(context);
    }
  };

  context.afterSlideHandler = () => {
    syncLightboxCounter(context);
  };

  context.afterCloseHandler = () => {
    hostEl.removeEventListener("lgBeforeSlide", context.beforeSlideHandler);
    hostEl.removeEventListener("lgAfterSlide", context.afterSlideHandler);
    hostEl.removeEventListener("lgAfterClose", context.afterCloseHandler);
    context.instance.destroy();
    hostEl.dispatchEvent(
      new CustomEvent("photo-gallery:resume", {
        bubbles: true,
      }),
    );
  };

  hostEl.addEventListener("lgBeforeSlide", context.beforeSlideHandler);
  hostEl.addEventListener("lgAfterSlide", context.afterSlideHandler);
  hostEl.addEventListener("lgAfterClose", context.afterCloseHandler);

  lg.openGallery(startIndex);
  requestAnimationFrame(() => syncLightboxCounter(context));
  loadMoreSlides(context);
}

const openSinglePhoto = (trigger) => {
  const data = [
    {
      src: trigger.href,
      subHtml: `<p>${escapeHtml(trigger.textContent || "")}</p>`,
    },
  ];

  const lg = lightGallery(trigger, {
    licenseKey,
    dynamic: true,
    dynamicEl: data,
    download: false,
    enableDrag: false,
    enableSwipe: false,
    hideBarsDelay: 0,
    addClass: "as-single-photo",
    counter: false,
  });

  lg.openGallery();
};

document.addEventListener("photo-gallery:open-lightbox", (e) => {
  const galleryEl = e.target.closest("[data-photo-gallery]");
  if (!galleryEl) return;

  const detail = e.detail || {};
  if (!detail.streamUrl) return;

  openPhotoGalleryLightbox(detail, galleryEl);
});

delegate(".lightbox-trigger", "click", (e) => {
  e.preventDefault();

  const trigger = e.target.closest(".lightbox-trigger");
  if (!trigger) return;

  openSinglePhoto(trigger);
});
